// Opportunity-Trichter (Agent 7A, R-11): Kennzahlenkarten und Balkentrichter je Status einer Pipeline, Anzahl oder Betrag.
// Betrachtet werden Opportunities, die im Zeitraum angelegt wurden; der Verlauf der Status wird nicht nachgerechnet.
import { useMemo, useState } from "react";
import { berichteApi } from "../api/berichte";
import { Laden } from "../components/Bausteine";
import { Auswahl, type Option } from "../components/Auswahl";
import { Icon } from "../components/Icon";
import { useMeta } from "../daten/meta";
import { BerichtSeite } from "./BerichtSeite";
import { useGeladen } from "./daten";
import { euroText, ganzText, zahlText } from "./format";
import type { Konfig } from "./konfig";
import { useBerichtSteuerung } from "./steuerung";
import { useBerichtFilter } from "./zustand";

export function Trichter() {
  const filter = useBerichtFilter("dieses_quartal");
  const { meta } = useMeta();
  const [pipeline, setPipeline] = useState<string | null>(null);
  const [wert, setWert] = useState<"anzahl" | "betrag">("anzahl");
  const zusatz = useMemo<Konfig>(() => ({ pipeline_id: pipeline, wert }), [pipeline, wert]);
  const steuerung = useBerichtSteuerung({
    art: "trichter", filter, zusatz,
    onAnwenden: (k) => {
      setPipeline(typeof k.pipeline_id === "string" ? k.pipeline_id : null);
      setWert(k.wert === "betrag" ? "betrag" : "anzahl");
    },
  });
  const { eingabe, eingabeSchluessel } = filter;
  const abfrage = useGeladen(
    () => berichteApi.trichter({ ...eingabe, vergleich: undefined, ...(pipeline ? { pipeline_id: pipeline } : {}), wert }),
    `${eingabeSchluessel}|${pipeline}|${wert}`,
    steuerung.geladen,
  );
  const a = abfrage.daten;
  const pipelines: Option[] = (meta?.pipelines ?? []).map((p) => ({ wert: p.id, label: p.name }));
  const gewaehlt = pipeline ?? a?.pipeline_id ?? null;
  const k = a?.karten ?? {};
  const maxStufe = Math.max(1, ...(a?.stufen ?? []).map((s) => (wert === "betrag" ? s.betrag : s.anzahl)));

  return (
    <BerichtSeite
      art="trichter"
      filter={filter}
      steuerung={steuerung}
      ohneVergleich
      fehler={abfrage.fehler}
      onNochmal={abfrage.neuLaden}
      csv={[{ titel: "Trichter als CSV laden", art: "trichter", parameter: { ...eingabe, vergleich: undefined, ...(pipeline ? { pipeline_id: pipeline } : {}), wert } }]}
      weitereFilter={
        <>
          {pipelines.length > 1 ? (
            <Auswahl
              optionen={pipelines}
              wert={gewaehlt}
              onWahl={(w) => setPipeline((w as string) || null)}
              titel="Pipeline wählen"
              knopfKlasse="filterchip filterchip--auswahl bericht-pille filterchip--an"
              knopfInhalt={
                <>
                  <Icon name="status" groesse={14} />
                  <span className="bericht-pille__text">{pipelines.find((p) => p.wert === gewaehlt)?.label ?? "Pipeline"}</span>
                  <Icon name="chevron-unten" groesse={12} />
                </>
              }
              breite={220}
            />
          ) : null}
          <div className="segment" role="group" aria-label="Anzahl oder Betrag">
            <button type="button" className={`segment__knopf${wert === "anzahl" ? " segment__knopf--an" : ""}`} onClick={() => setWert("anzahl")}>Anzahl</button>
            <button type="button" className={`segment__knopf${wert === "betrag" ? " segment__knopf--an" : ""}`} onClick={() => setWert("betrag")}>Betrag</button>
          </div>
        </>
      }
    >
      {!a ? (
        <Laden />
      ) : (
        <div aria-busy={abfrage.laedt}>
          <div className="kachelraster kachelraster--karten">
            <div className="kachel"><div className="kachel__zahl">{ganzText(k.opportunities)}</div><div className="kachel__titel kachel__titel--fest">Opportunities</div><div className="kachel__unter">ANGELEGT IM ZEITRAUM</div></div>
            <div className="kachel"><div className="kachel__zahl">{k.abschlussquote === null || k.abschlussquote === undefined ? "–" : `${zahlText(k.abschlussquote)} %`}</div><div className="kachel__titel kachel__titel--fest">Abschlussquote</div><div className="kachel__unter">GEWONNEN VON ABGESCHLOSSEN</div></div>
            <div className="kachel"><div className="kachel__zahl">{k.dauer_bis_gewinn_tage === null || k.dauer_bis_gewinn_tage === undefined ? "–" : `${zahlText(k.dauer_bis_gewinn_tage)} Tage`}</div><div className="kachel__titel kachel__titel--fest">Zeit bis zum Gewinn</div><div className="kachel__unter">IM SCHNITT</div></div>
            <div className="kachel"><div className="kachel__zahl">{euroText(k.wert_je_gewinn)}</div><div className="kachel__titel kachel__titel--fest">Wert je Gewinn</div><div className="kachel__unter">IM SCHNITT, JÄHRLICH</div></div>
            <div className="kachel"><div className="kachel__zahl">{k.sales_velocity === null || k.sales_velocity === undefined ? "–" : `${euroText(k.sales_velocity)}/Tag`}</div><div className="kachel__titel kachel__titel--fest">Sales Velocity</div><div className="kachel__unter" title="Opportunities mal Abschlussquote mal Wert je Gewinn, geteilt durch die Tage bis zum Gewinn">EURO PRO TAG</div></div>
          </div>
          <section className="bkarte trichter">
            <header className="bkarte__kopf">
              <h2 className="bkarte__titel">{a.pipeline_name ?? "Pipeline"} · {wert === "betrag" ? "Betrag" : "Anzahl"} je Status</h2>
            </header>
            {a.stufen.length === 0 ? (
              <p className="leise">Für diese Pipeline gibt es keine Status.</p>
            ) : (
              <ul className="trichter__liste">
                {a.stufen.map((s) => {
                  const zahl = wert === "betrag" ? s.betrag : s.anzahl;
                  return (
                    <li key={s.status_id} className="trichter__zeile">
                      <span className="trichter__label" title={s.label}>{s.label}</span>
                      <span className="trichter__bahn" aria-hidden="true">
                        <span className={`trichter__balken trichter__balken--${s.typ}`} style={{ width: `${Math.max(zahl > 0 ? 2 : 0, (zahl / maxStufe) * 100)}%` }} />
                      </span>
                      <span className="trichter__zahl">{wert === "betrag" ? euroText(s.betrag) : ganzText(s.anzahl)}</span>
                      <span className="trichter__erreicht leise" title="So viele haben diese Stufe erreicht oder sind weiter">
                        {s.typ === "active" && s.erreicht !== undefined ? `erreicht: ${ganzText(s.erreicht)}` : ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="leise bkarte__unter">{a.hinweis} Beträge sind jährlich hochgerechnet (monatlich mal 12). Aktiv: {euroText(k.wert_aktiv)} · Gewonnen: {euroText(k.wert_gewonnen)}.</p>
          </section>
        </div>
      )}
    </BerichtSeite>
  );
}
